<template>
    <div class="padding-normal" :style="{ backgroundColor: backgroundColor?.hex || '' }">
        <div>
            <h3 class="normal-container mb-4 !text-lg lg:mb-20 lg:!text-3xl">
                {{ title }}
            </h3>
            <div class="relative w-full overflow-hidden">
                <div class="normal-container">
                    <LazyUiSlider>
                        <div
                            v-for="(item, index) in items"
                            :class="['keen-slider__slide number-slide' + index]"
                            class="min-w-[165px] max-w-[165px] lg:min-w-[224px] lg:max-w-[224px]">
                            <div
                                class="group !flex min-w-[165px] max-w-[165px] flex-col items-center justify-center lg:min-w-[224px] lg:max-w-[224px]">
                                <template v-if="item?.pageReference._ref && getUrlById(item.pageReference._ref)">
                                    <NuxtLink :to="getUrlById(item.pageReference._ref) || '#'" class="block">
                                        <div class="flex flex-col items-center justify-center">
                                            <div
                                                :class="[
                                                    showBackground ? 'bg-green-550' : 'overflow-hidden',
                                                    'flex h-[220px] w-[165px] items-center justify-center rounded-[109px] lg:h-[300px] lg:w-[224px]',
                                                ]"
                                                class="m=px-4">
                                                <NuxtImg
                                                    v-if="item && item.itemImage"
                                                    class="aspect-[3/4] transform object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
                                                    :class="showBackground ? 'my-4 aspect-[3/4]' : 'h-full w-full'"
                                                    :src="getImage(item.itemImage)"
                                                    :alt="item.itemImage.alt"
                                                    width="225"
                                                    height="300"
                                                    sizes="(min-width: 1200px) 600px, (min-width: 992px) 450px, (min-width: 662px) 225px, 225px"
                                                    placeholder
                                                    format="webp"
                                                    loading="lazy" />
                                            </div>

                                            <h2
                                                class="mt-4 inline-block border-b-2 border-transparent text-center !text-sm lg:mt-10 lg:!text-lg"
                                                :class="{
                                                    'underline-active group-hover:border-customGreen': showUnderline,
                                                }">
                                                {{ item.itemTitle }}
                                            </h2>
                                        </div>
                                    </NuxtLink>
                                </template>
                                <template v-else>
                                    <div class="flex flex-col items-center justify-center">
                                        <div
                                            :class="[
                                                showBackground ? 'bg-green-550' : 'overflow-hidden',
                                                'flex h-[220px] w-[165px] items-center justify-center rounded-[109px] lg:h-[300px] lg:w-[224px]',
                                            ]"
                                            class="m=px-4">
                                            <NuxtImg
                                                v-if="item && item.itemImage"
                                                class="aspect-[3/4] transform object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
                                                :class="showBackground ? 'my-4 aspect-[3/4]' : 'h-full w-full'"
                                                :src="getImage(item.itemImage)"
                                                :alt="item.itemImage.alt"
                                                width="100%"
                                                height="100%"
                                                sizes="(min-width: 1200px) 600px, (min-width: 992px) 450px, (min-width: 662px) 226px, 226px"
                                                placeholder
                                                format="webp"
                                                loading="lazy" />
                                        </div>

                                        <h2
                                            class="mt-4 inline-block border-b-2 border-transparent text-center !text-sm lg:mt-10 lg:!text-lg"
                                            :class="{
                                                'underline-active group-hover:border-customGreen': showUnderline,
                                            }">
                                            {{ item.itemTitle }}
                                        </h2>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </LazyUiSlider>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { useKeenSlider } from 'keen-slider/vue';
    import { ArrowRightIcon, ArrowLeftIcon } from '@heroicons/vue/24/outline';

    const props = defineProps({
        image: Object,
        title: String,
        items: Array,
        product: Object,
        showBackground: Boolean,
        backgroundColor: String,
        showUnderline: Boolean,
    });

    const { getUrlById } = useCategorySeoUrl();

    function getImage(value: any) {
        return value.url ? value.url : useImageUrl(value).format('webp').width(300).quality(65).url();
    }

    const itemIds = props.items
        .map(item => {
            try {
                return item.pageReference?._ref;
            } catch (error) {
                console.error('An error occurred:', error);
                return null;
            }
        })
        .filter(id => id !== null);

    const brandIds = props.items
        .map(item => {
            try {
                return item.selectedBrand?._ref;
            } catch (error) {
                console.error('An error occurred:', error);
                return null;
            }
        })
        .filter(id => id !== null);

    const seoUrls = ref({});
    const brandUrls = ref({});

    const fetchSeoUrls = async () => {
        try {
            const ids = ref(itemIds);
            const data = await useGetShopwarePageById(ids);
            seoUrls.value = data;
            return data;
        } catch (error) {
            console.error('Error fetching SEO URLs:', error);
        }
    };

    const fetchBrandUrls = async () => {
        try {
            const data = await useBrandUrls(brandIds);
            brandUrls.value = Object.fromEntries(Object.entries(data).map(([id, { url }]) => [id, url]));
        } catch (error) {
            console.error('Error fetching brand URLs:', error);
        }
    };

    onMounted(() => {
        fetchBrandUrls();
    });

    function getUrl(item: any) {
        try {
            if (item.externalLink) return item.externalLink;
            else if (item.pageReference && item.pageReference?._ref) {
                // if item is undefined, return null
                if (!seoUrls.value[item.pageReference?._ref]) return null;
                // else return the seoUrls value of the item.pageReference._ref
                return '/' + seoUrls.value[item.pageReference?._ref] || '#';
            } else if (item.selectedBrand && item.selectedBrand?._ref) {
                return brandUrls.value[item.selectedBrand?._ref] || '#';
            }
        } catch (error) {
            console.error('An error occurred:', error);
        }
        return null;
    }
</script>
